import { ENDPOINTS } from "./endpoints";
import { getClientBrowserInfo } from "./clientBrowserInfo";

let ctx = {
  getTelemetryJwt: () => null,
  getRoomContext: () => ({ roomName: "", reporterIdentity: "" }),
};

export function setClientLogContext(partial) {
  ctx = { ...ctx, ...partial };
}

function clientLogBrowserEnvelope() {
  if (typeof navigator === "undefined") return {};
  const b = getClientBrowserInfo();
  const env = {
    userAgent: navigator.userAgent || "",
    browserType: b.browserType,
    browserVersion: b.browserVersion,
    deviceModel: b.deviceModel,
    deviceClass: b.deviceClass,
    platform: navigator.platform || undefined,
    language: navigator.language || undefined,
  };
  if (typeof b.mobile === "boolean") env.mobile = b.mobile;
  return env;
}

function sendPayload(body) {
  const jwt = ctx.getTelemetryJwt();
  const { roomName, reporterIdentity } = ctx.getRoomContext();
  if (!jwt || !roomName || !reporterIdentity) return;
  fetch(ENDPOINTS.CLIENT_LOGS, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({
      roomName,
      reporterIdentity,
      ts: new Date().toISOString(),
      url: typeof window !== "undefined" ? window.location?.href : "",
      ...clientLogBrowserEnvelope(),
      ...body,
    }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Manual report from app code (LiveKit errors, audio state, etc.).
 */
export function reportClientLog(level, message, extra = {}) {
  sendPayload({ level, message: String(message).slice(0, 4000), ...extra });
}

/**
 * Use in catch blocks so failures are logged to the console and (when in a room) to the server.
 */
export function captureException(reason, source = "caught") {
  const err =
    reason instanceof Error
      ? reason
      : new Error(typeof reason === "string" ? reason : JSON.stringify(reason));
  console.error(`[${source}]`, err);
  reportClientLog(`caught_${source}`, err.message, { stack: err.stack });
}

let consoleThrottle = 0;
const CONSOLE_THROTTLE_MS = 4000;
const CONSOLE_LOG_THROTTLE_MS = 12000;

function forwardConsole(level, args) {
  const now = Date.now();
  if (now - consoleThrottle < CONSOLE_THROTTLE_MS) return;
  consoleThrottle = now;
  const text = args.map((a) => (typeof a === "string" ? a : String(a))).join(" ").slice(0, 2000);
  sendPayload({ level, message: text });
}

let logThrottle = 0;
function forwardConsoleLog(args) {
  if (process.env.REACT_APP_CLIENT_LOG_FORWARD_CONSOLE_LOG !== "true") return;
  const now = Date.now();
  if (now - logThrottle < CONSOLE_LOG_THROTTLE_MS) return;
  logThrottle = now;
  const text = args.map((a) => (typeof a === "string" ? a : String(a))).join(" ").slice(0, 2000);
  sendPayload({ level: "console_log", message: text });
}

/**
 * One-time global handlers; context supplied via setClientLogContext from Room.
 */
export function installClientLogBridge() {
  if (typeof window === "undefined" || window.__lkClientLogBridge) return;
  window.__lkClientLogBridge = true;

  window.addEventListener("error", (ev) => {
    sendPayload({
      level: "error",
      message: ev.message || "window.error",
      stack: ev.error?.stack,
      context: { filename: ev.filename, lineno: ev.lineno, colno: ev.colno },
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const r = ev.reason;
    sendPayload({
      level: "error",
      message: r?.message || String(r),
      stack: r?.stack,
    });
  });

  const origErr = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  const origLog = console.log.bind(console);
  console.error = (...args) => {
    forwardConsole("console_error", args);
    origErr(...args);
  };
  console.warn = (...args) => {
    forwardConsole("console_warn", args);
    origWarn(...args);
  };
  console.log = (...args) => {
    forwardConsoleLog(args);
    origLog(...args);
  };
}
